/**
 * GeoDirectory Map Initializer
 *
 * Modern map initialization for GeoDirectory v3.
 * Handles directory maps, archive maps, post maps, and directions.
 */

import { MapFactory } from './core/MapFactory.js';
import { MarkerManager } from './MarkerManager.js';
import { Popup } from './features/Popup.js';
import { MapFilters } from './MapFilters.js';
import { MarkerInteractions } from './features/MarkerInteractions.js';

/**
 * MapInitializer class
 * Provides modern map initialization functionality.
 */
export class MapInitializer {
	/**
	 * Initialize a directory or archive map with AJAX search functionality.
	 *
	 * @param {string} mapCanvas - The map canvas element ID
	 * @param {boolean} loadTerms - Whether to load category terms on init
	 * @param {object} options - Map configuration options
	 */
	static initAjaxSearchMap(mapCanvas, loadTerms = true, options = {}) {

		const element = document.getElementById(mapCanvas);
		if (!element) {
			return null;
		}

		// Helper function to show error and hide loading
		const showMapError = () => {
			const loadingDiv = document.getElementById(mapCanvas + '_loading_div');
			if (loadingDiv) {
				loadingDiv.style.display = 'none';
			}
			const errorDiv = document.getElementById(mapCanvas + '_map_notloaded');
			if (errorDiv) {
				errorDiv.style.display = '';
			}
		};

		// Get configuration from window globals (set by PHP) or element data attributes
		let latitude = parseFloat(window.gdBaseLat) || 0;
		let longitude = parseFloat(window.gdBaseLng) || 0;
		let zoom = parseInt(window.gdBaseZoom) || 11;

		// Fallback to data attributes if window globals are not set
		if (latitude === 0 && longitude === 0) {
			if (element.dataset.lat && element.dataset.lng) {
				latitude = parseFloat(element.dataset.lat);
				longitude = parseFloat(element.dataset.lng);
			}
		}

		const config = {
			latitude: latitude,
			longitude: longitude,
			zoom: zoom,
			preferredProvider: window.gdSetMap || 'auto',
			hideExpandMap: element.dataset.hideExpandMap === '1',
			scrollwheel: element.dataset.scrollwheel !== '0',
			zoomControl: element.dataset.zoomControl !== '0',
			maptype: element.dataset.maptype || 'ROADMAP',
			...options
		};


		if (config.latitude === 0 && config.longitude === 0) {
		}

		// Create the map using MapFactory
		const provider = MapFactory.create(mapCanvas, config);

		if (!provider) {
			showMapError();
			return null;
		}

		// Detect map type before init
		const mapType = element.dataset.mapType || 'directory';

		// For post maps (single marker), allow tiles to hide loading immediately
		if (mapType === 'post' || (element.dataset.lat && element.dataset.lng)) {
			provider.allowTileHideLoading = true;
		}

		// Initialize the map
		if (!provider.init()) {
			showMapError();
			return null;
		}

		// If this is a post map (has coordinates in data attributes), add a single marker
		if (mapType === 'post' || (element.dataset.lat && element.dataset.lng)) {

			const markerOptions = {
				lat: config.latitude,
				lng: config.longitude,
				title: element.dataset.title || '',
				draggable: false
			};

			// Add icon data if available from data attributes
			if (element.dataset.markerIcon) {
				markerOptions.icon = element.dataset.markerIcon;
				markerOptions.w = parseInt(element.dataset.markerW) || 36;
				markerOptions.h = parseInt(element.dataset.markerH) || 45;
			}

			const marker = provider.createMarker(markerOptions);

			// Add click handler to open popup
			const postId = element.dataset.postId || window.geodir_post_id;
			if (postId && provider.addMarkerListener) {
				provider.addMarkerListener(marker, 'click', () => {
					Popup.fetchAndOpenPopup(provider, marker, postId, {
						restUrl: window.geodir_map_params?.rest_url || '/wp-json/geodir/v3',
						restNonce: window.geodir_map_params?.rest_nonce || ''
					});
				});
			}
		}
		// Archive map - load markers from listing post IDs
		else if (mapType === 'archive') {

			// Get post type from data attribute
			const postType = element.dataset.postType || window.geodir_post_type || 'gd_place';

			// Create MarkerManager and load archive markers
			const markerManager = new MarkerManager(provider);
			markerManager.loadArchiveMarkers(postType).then(response => {

				// If we got markers, fit the map to show them all
				if (response.items && response.items.length > 0) {
					// Map will be centered on markers by MarkerManager

					// Initialize marker interactions (bounce on listing hover)
					const scrollOnHover = element.dataset.scrollOnHover === '1';
					const markerInteractions = new MarkerInteractions(markerManager, {
						scrollOnHover: scrollOnHover
					});
					markerInteractions.init();
				} else {
				}
			}).catch(error => {
			});

			// Store MarkerManager for later use (filtering, etc.)
			window.geodirMaps = window.geodirMaps || {};
			window.geodirMaps[mapCanvas + '_markerManager'] = markerManager;
		}
		// Directory map - load markers with optional filters
		else if (mapType === 'directory') {

			// Get post type from data attribute
			const postType = element.dataset.postType || window.geodir_post_type || 'gd_place';

			// Get REST API configuration
			const restUrl = element.dataset.restUrl || window.geodir_map_params?.rest_url || '/wp-json/geodir/v3';
			const restNonce = element.dataset.restNonce || window.geodir_map_params?.rest_nonce || '';

			// Create MarkerManager with config
			const markerManager = new MarkerManager(provider, {
				restUrl: restUrl,
				restNonce: restNonce
			});

			// Check if any filters are enabled
			const hasCatFilter = element.dataset.catFilter === '1';
			const hasSearchFilter = element.dataset.searchFilter === '1';
			const hasPostTypeFilter = element.dataset.postTypeFilter === '1';
			const childCollapse = element.dataset.childCollapse === '1';
			const terms = element.dataset.terms || '';
			const tickTerms = element.dataset.tickTerms || '';
			const tags = element.dataset.tags || '';


			// Load filters if enabled
			if ((hasCatFilter || hasSearchFilter || hasPostTypeFilter) && loadTerms) {

				// Try to build from embedded data first (no API call)
				let filters = MapFilters.buildFromEmbeddedData(mapCanvas, { childCollapse });

				if (filters) {

					// Inject into map UI
					MapFilters.injectIntoMap(filters, mapCanvas);

					// Setup change handlers
					if (filters.categoryTree) {
						MapFilters.setupCategoryChangeHandler(mapCanvas, markerManager);
					}

					if (filters.postTypeDropdown) {
						MapFilters.setupPostTypeChangeHandler(mapCanvas, markerManager, {
							restUrl,
							restNonce,
							childCollapse,
							terms,
							tickTerms,
							tags,
							searchFilter: hasSearchFilter,
							postTypeFilter: hasPostTypeFilter
						});
					}

					if (filters.searchInput) {
						MapFilters.setupSearchInputHandler(mapCanvas, markerManager, postType, {
							tags
						});
					}

					// Setup filter minimize/maximize toggle
					MapFilters.setupFilterToggle(mapCanvas);

					// Now load markers with initial category selection
					const selectedCategories = MapFilters.getSelectedCategories(mapCanvas);

					markerManager.loadDirectoryMarkers(postType, {
						term: selectedCategories,
						tag: tags ? tags.split(',') : []
					}).then(response => {

						// Initialize marker interactions (bounce on listing hover)
						if (response.items && response.items.length > 0) {
							const scrollOnHover = element.dataset.scrollOnHover === '1';
							const markerInteractions = new MarkerInteractions(markerManager, {
								scrollOnHover: scrollOnHover
							});
							markerInteractions.init();
						}
					}).catch(error => {
					});
				} else {
					// Fallback to API call if embedded data not available

					MapFilters.fetchAndBuild({
						mapCanvas: mapCanvas,
						postType: postType,
						restUrl: restUrl,
						restNonce: restNonce,
						childCollapse: childCollapse,
						terms: terms,
						tickTerms: tickTerms,
						searchFilter: hasSearchFilter,
						postTypeFilter: hasPostTypeFilter
					}).then(filters => {
						if (filters) {
							// Inject into map UI
							MapFilters.injectIntoMap(filters, mapCanvas);

							// Setup change handlers
							if (filters.categoryTree) {
								MapFilters.setupCategoryChangeHandler(mapCanvas, markerManager);
							}

							if (filters.postTypeDropdown) {
								MapFilters.setupPostTypeChangeHandler(mapCanvas, markerManager, {
									restUrl,
									restNonce,
									childCollapse,
									terms,
									tickTerms,
									tags,
									searchFilter: hasSearchFilter,
									postTypeFilter: hasPostTypeFilter
								});
							}

							if (filters.searchInput) {
								MapFilters.setupSearchInputHandler(mapCanvas, markerManager, postType, {
									tags
								});
							}

							// Setup filter minimize/maximize toggle
							MapFilters.setupFilterToggle(mapCanvas);

							// Now load markers with initial category selection
							const selectedCategories = MapFilters.getSelectedCategories(mapCanvas);

							markerManager.loadDirectoryMarkers(postType, {
								term: selectedCategories,
								tag: tags ? tags.split(',') : []
							}).then(response => {

								// Initialize marker interactions (bounce on listing hover)
								if (response.items && response.items.length > 0) {
									const scrollOnHover = element.dataset.scrollOnHover === '1';
									const markerInteractions = new MarkerInteractions(markerManager, {
										scrollOnHover: scrollOnHover
									});
									markerInteractions.init();
								}
							}).catch(error => {
							});
						} else {
						}
					}).catch(error => {
					});
				}
			} else if (loadTerms) {
				// No filters enabled, just load all markers
				markerManager.loadDirectoryMarkers(postType, {
					tag: tags ? tags.split(',') : []
				}).then(response => {

					// Initialize marker interactions (bounce on listing hover)
					if (response.items && response.items.length > 0) {
						const scrollOnHover = element.dataset.scrollOnHover === '1';
						const markerInteractions = new MarkerInteractions(markerManager, {
							scrollOnHover: scrollOnHover
						});
						markerInteractions.init();
					}
				}).catch(error => {
				});
			} else {
			}

			// Store MarkerManager for later use (filtering, etc.)
			window.geodirMaps = window.geodirMaps || {};
			window.geodirMaps[mapCanvas + '_markerManager'] = markerManager;
		}

		// Note: Loading div is now hidden by map provider after tiles load
		// Removed premature hide that was causing gray background flash

		// Store map instance globally for access
		window.geodirMaps = window.geodirMaps || {};
		window.geodirMaps[mapCanvas] = provider;


		return provider;
	}

	/**
	 * Initialize a static post map (single location marker).
	 *
	 * @param {string} mapCanvas - The map canvas element ID
	 * @param {object} options - Map configuration options
	 */
	static initStaticMap(mapCanvas, options = {}) {

		const element = document.getElementById(mapCanvas);
		if (!element) {
			return null;
		}

		// Helper function to show error and hide loading
		const showMapError = () => {
			const loadingDiv = document.getElementById(mapCanvas + '_loading_div');
			if (loadingDiv) {
				loadingDiv.style.display = 'none';
			}
			const errorDiv = document.getElementById(mapCanvas + '_map_notloaded');
			if (errorDiv) {
				errorDiv.style.display = '';
			}
		};

		// Get configuration from window globals (set by PHP) or element data attributes
		let latitude = parseFloat(window.gdBaseLat) || 0;
		let longitude = parseFloat(window.gdBaseLng) || 0;
		let zoom = parseInt(window.gdBaseZoom) || 11;

		// Fallback to data attributes if window globals are not set
		if (latitude === 0 && longitude === 0) {
			if (element.dataset.lat && element.dataset.lng) {
				latitude = parseFloat(element.dataset.lat);
				longitude = parseFloat(element.dataset.lng);
			}
		}

		const config = {
			latitude: latitude,
			longitude: longitude,
			zoom: zoom,
			preferredProvider: window.gdSetMap || 'auto',
			hideExpandMap: element.dataset.hideExpandMap === '1',
			scrollwheel: element.dataset.scrollwheel !== '0',
			zoomControl: element.dataset.zoomControl !== '0',
			maptype: element.dataset.maptype || 'ROADMAP',
			...options
		};


		if (config.latitude === 0 && config.longitude === 0) {
		}

		// Create the map using MapFactory
		const provider = MapFactory.create(mapCanvas, config);

		if (!provider) {
			showMapError();
			return null;
		}

		// For static maps, allow tiles to hide loading immediately
		provider.allowTileHideLoading = true;

		// Initialize the map
		if (!provider.init()) {
			showMapError();
			return null;
		}

		// Add a single marker at the center
		const marker = provider.createMarker({
			lat: config.latitude,
			lng: config.longitude,
			title: options.markerTitle || '',
			draggable: false
		});

		// Store map instance globally for access
		window.geodirMaps = window.geodirMaps || {};
		window.geodirMaps[mapCanvas] = provider;


		return provider;
	}

	/**
	 * Initialize map directions functionality.
	 *
	 * @param {string} mapCanvas - The map canvas element ID
	 */
	static initDirections(mapCanvas) {

		const mapProvider = window.geodirMaps?.[mapCanvas];
		if (!mapProvider) {
			return;
		}

		// TODO: Implement directions functionality
		// This will need to:
		// 1. Add directions panel UI
		// 2. Handle directions requests
		// 3. Display route on map
		// 4. Show turn-by-turn directions

	}

	/**
	 * Get a map instance by canvas ID.
	 *
	 * @param {string} mapCanvas - The map canvas element ID
	 * @returns {object|null} The map provider instance
	 */
	static getMap(mapCanvas) {
		return window.geodirMaps?.[mapCanvas] || null;
	}
}

// Expose globally for inline scripts (temporary compatibility)
window.geodirInitAjaxSearchMap = (mapCanvas, loadTerms, options) => {
	return MapInitializer.initAjaxSearchMap(mapCanvas, loadTerms, options);
};

window.geodirInitStaticMap = (mapCanvas, options) => {
	return MapInitializer.initStaticMap(mapCanvas, options);
};

window.geodirInitDirections = (mapCanvas) => {
	return MapInitializer.initDirections(mapCanvas);
};

